import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-semibold text-[15px] rounded-pill border-[1.5px] border-transparent cursor-pointer whitespace-nowrap transition-colors duration-short ease-default active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-electric-700 focus-visible:outline-offset-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100",
  {
    variants: {
      variant: {
        primary:
          "bg-electric-700 text-white border-electric-700 hover:bg-electric-800 hover:border-electric-800 active:bg-electric-800 active:border-electric-800",
        secondary:
          "bg-transparent text-ink border-transparent hover:bg-electric-50 hover:text-electric-800 active:bg-electric-50 active:text-electric-800",
        tertiary:
          "bg-transparent text-electric-800 px-2 h-9 hover:underline hover:underline-offset-[3px]",
        destructive: "bg-danger text-white border-danger",
        ghost: "bg-fog text-ink hover:bg-fog-dark",
      },
      size: {
        sm: "h-8 px-3.5 text-[13px]",
        default: "h-10 px-5",
        lg: "h-12 px-6 text-base",
        icon: "size-9 p-0",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  },
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  asChild?: boolean;
}

function Button({
  className,
  variant,
  size,
  loading,
  asChild,
  disabled,
  type = "button",
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={loading || disabled}
      {...props}
    >
      {loading ? (
        <>
          <svg className="size-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

export { Button, type ButtonProps, buttonVariants };
